import { useEffect, useState, useCallback, useRef } from "react";
import { Skeleton, SkeletonField } from './Skeleton';
import {
  User,
  PanelLeft,
  Edit3,
  X,
  Save,
  Camera,
  MapPin,
  Clock,
  Lightbulb,
  ChevronRight,
  Zap,
  Bot,
} from "lucide-react";
import { apiClient } from "../apiClient";
import "./ProfileView.css";
import { type BackendUserWithPlan, type BackendPlan, fetchBillingPortalUrl } from "../userService";
import { fetchPlans } from "../plansService";
import PlansView from "./PlansView";
import PlanManagementView from "./PlanManagementView";

type ProfileViewProps = {
  onMenuClick: () => void;
  showMenu: boolean;
  userData?: BackendUserWithPlan | null;
};

function ProfileView({ onMenuClick, showMenu, userData }: ProfileViewProps) {
  // User profile state - populated from /api/user, start empty until loaded
  const contentRef = useRef<HTMLDivElement>(null);
  const [background, setBackground] = useState<string>("");
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    location: "",
    experience: "",
    focus_areas: "",
    desire: "",
    anxieties: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPlanManagement, setShowPlanManagement] = useState(false);
  const [showAvailablePlans, setShowAvailablePlans] = useState(false);
  const [plans, setPlans] = useState<BackendPlan[]>([]);
  const [backgroundOptions, setBackgroundOptions] = useState<string[]>([]);
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    show_online_status: true,
    dark_mode: true,
  });

  // Load preferences from API
  useEffect(() => {
    apiClient.get('/api/user/preferences/').then(res => {
      setPreferences(res.data);
    }).catch(() => {
      // Fallback to localStorage
      setPreferences({
        email_notifications: localStorage.getItem('pref_email_notifications') !== 'false',
        show_online_status: localStorage.getItem('pref_show_online') !== 'false',
        dark_mode: localStorage.getItem('pref_dark_mode') !== 'false',
      });
    });
  }, []);

  const updatePreference = (key: keyof typeof preferences, value: boolean) => {
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);
    localStorage.setItem(`pref_${key}`, String(value));
    apiClient.patch('/api/user/preferences/', { [key]: value }).catch(() => {});
    if (key === 'dark_mode') {
      document.documentElement.setAttribute('data-theme', value ? 'dark' : 'light');
    }
  };

  useEffect(() => {
    const loadPlans = async () => {
      const fetchedPlans = await fetchPlans();
      const enabledPlans = fetchedPlans.filter(p => p.enabled);
      setPlans(enabledPlans);
    };
    loadPlans();
    apiClient.get<string[]>('/api/user/backgrounds/').then(res => {
      setBackgroundOptions(res.data);
    }).catch(() => {});

    // Listen for upgrade button click from sidebar
    const handleOpenPlans = () => setShowAvailablePlans(true);
    window.addEventListener('open-plans', handleOpenPlans);
    return () => window.removeEventListener('open-plans', handleOpenPlans);
  }, []);

  const goToBilling = async () => {
    const url = await fetchBillingPortalUrl();
    if (!url) {
      return;
    }

    window.location.href = url;
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiClient.put("/api/profile/", {
        first_name: profile.name.split(" ")[0] || "",
        last_name: profile.name.split(" ").slice(1).join(" ") || "",
        email: profile.email,
        profile: {
          background,
          location: profile.location,
          experience: profile.experience,
        },
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save profile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const experienceLevels = [
    "Less than 1 year",
    "1-3 years",
    "3-5 years",
    "5-10 years",
    "10+ years",
  ];

  const mapIndustryKnowledgeToExperience = (
    value: number | null | undefined,
  ): string | null => {
    if (value == null) return null;
    if (value <= 1) return "Less than 1 year";
    if (value === 2) return "1-3 years";
    if (value === 3) return "3-5 years";
    if (value === 4) return "5-10 years";
    return "10+ years";
  };

  // Helper to format price
  const formatPlanPrice = (plan: BackendPlan) => {
    const numericPrice = parseFloat(plan.price);
    if (numericPrice === 0) return "Free";
    
    const currencySymbol = plan.currency === 'AUD' ? '$' : '';
    const roundedPrice = Math.round(numericPrice);
    const interval = plan.billing_interval === 1 ? '/month' : '';
    
    return `${currencySymbol}${roundedPrice}${interval}`;
  };

  // Helper to map backend user to local state
  const mapUserDataToState = useCallback((user: BackendUserWithPlan) => {
    const fullName =
      (user.first_name || user.last_name)
        ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
        : user.username ?? "";

    const locationFromApi =
      user.profile &&
      typeof user.profile.location === "string" &&
      user.profile.location.trim() !== ""
        ? user.profile.location
        : "";

    const experienceFromApiString =
      user.profile &&
      typeof user.profile.experience === "string" &&
      user.profile.experience.trim() !== ""
        ? user.profile.experience
        : null;

    const experienceFromIndustry =
      experienceFromApiString ??
      mapIndustryKnowledgeToExperience(user.profile?.industry_knowledge) ??
      "";

    const focusAreasFromApi =
      user.profile &&
      typeof user.profile.focus_areas === "string" &&
      user.profile.focus_areas.trim() !== ""
        ? user.profile.focus_areas.trim()
        : "";

    const desireFromApi =
      user.profile &&
      typeof user.profile.desire === "string" &&
      user.profile.desire.trim() !== ""
        ? user.profile.desire.trim()
        : "";

    const anxietiesFromApi =
      user.profile &&
      typeof user.profile.anxieties === "string" &&
      user.profile.anxieties.trim() !== ""
        ? user.profile.anxieties.trim()
        : "";

    setProfile({
      name: fullName,
      email: user.email ?? "",
      location: locationFromApi,
      experience: experienceFromIndustry ?? "",
      focus_areas: focusAreasFromApi,
      desire: desireFromApi,
      anxieties: anxietiesFromApi,
    });

    setBackground(
      user.profile && typeof user.profile.background === "string" && user.profile.background.trim() !== ""
        ? user.profile.background
        : ""
    );
  }, []);

  // Synchronize local state with userData prop when it changes,
  // but only if the user isn't currently editing.
  useEffect(() => {
    if (userData && !isEditing) {
      mapUserDataToState(userData);
    }
  }, [userData, isEditing, mapUserDataToState]);

  // Derive current plan details dynamically from backend data
  const activeBackendPlan = userData?.active_plan?.plan || plans.find(p => p.name.toLowerCase().includes("free") || p.name.toLowerCase().includes("basic") || parseFloat(p.price) === 0);
  
  const maxPrecedence = plans.length > 0 ? Math.max(...plans.map(p => p.precedence || 0)) : 0;
  const isPremium = activeBackendPlan && activeBackendPlan.precedence === maxPrecedence && maxPrecedence > 0;
  
  const currentPlanDetails = {
    name: activeBackendPlan?.name || "Chiefy Assists",
    price: activeBackendPlan ? formatPlanPrice(activeBackendPlan) : "Free",
    features: activeBackendPlan?.features || [],
    isPremium
  };

  // Available Plans Page
  if (showAvailablePlans) {
    return (
      <PlansView
        plans={plans}
        userData={userData}
        onBack={() => setShowAvailablePlans(false)}
      />
    );
  }

  // Plan Management Page
  if (showPlanManagement) {
    return (
      <PlanManagementView
        currentPlanDetails={currentPlanDetails}
        onBack={() => setShowPlanManagement(false)}
        onManagePlan={() => setShowAvailablePlans(true)}
        onGoToBilling={goToBilling}
      />
    );
  }

  return (
    <div className="profile-view">
      {/* Header */}
      <header className="profile-header" onClick={() => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}>
        {showMenu && (
          <button
            type="button"
            className="menu-btn"
            onClick={onMenuClick}
            aria-label="Open menu"
          >
            <PanelLeft size={20} />
          </button>
        )}
        <div className="header-content">
          <span className="header-icon">
            <User size={24} />
          </span>
          <h1 className="header-title">Profile</h1>
        </div>
        <div className="header-actions">
          {isEditing ? (
            <>
              <button
                type="button"
                className="header-btn cancel-btn"
                onClick={() => setIsEditing(false)}
              >
                <X size={18} />
                <span>Cancel</span>
              </button>
              <button
                type="button"
                className="header-btn save-btn"
                onClick={handleSave}
                disabled={isSaving}
              >
                <Save size={18} />
                <span>{isSaving ? "Saving..." : "Save"}</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              className="header-btn edit-btn"
              onClick={() => setIsEditing(true)}
            >
              <Edit3 size={18} />
              <span>Edit</span>
            </button>
          )}
        </div>
      </header>

      <div className="profile-content" ref={contentRef}>
        {!userData ? (
          <section className="profile-card profile-skeleton">
            <div className="skeleton-card-header">
              <Skeleton width="80px" height="80px" borderRadius="50%" />
              <div className="skeleton-card-info">
                <Skeleton width="50%" height="20px" />
                <Skeleton width="30%" height="14px" />
              </div>
            </div>
            <SkeletonField />
            <SkeletonField />
            <SkeletonField />
            <SkeletonField />
          </section>
        ) : <>
        {/* Profile Card */}
        <section className="profile-card">
          <div className="profile-avatar">
            <span className="avatar-text">
              {profile.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </span>
            {isEditing && (
              <button
                type="button"
                className="avatar-edit"
                aria-label="Change avatar"
              >
                <Camera size={16} />
              </button>
            )}
          </div>
          <div className="profile-info">
            {isEditing ? (
              <div className="edit-form">
                <div className="form-group">
                  <label htmlFor="profile-name">Full Name</label>
                  <input
                    id="profile-name"
                    type="text"
                    value={profile.name}
                    onChange={(e) =>
                      setProfile({ ...profile, name: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="profile-email">Email</label>
                  <input
                    id="profile-email"
                    type="email"
                    value={profile.email}
                    onChange={(e) =>
                      setProfile({ ...profile, email: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="profile-location">Location</label>
                  <input
                    id="profile-location"
                    type="text"
                    value={profile.location}
                    onChange={(e) =>
                      setProfile({ ...profile, location: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="profile-experience">Experience</label>
                  <select
                    id="profile-experience"
                    value={profile.experience}
                    onChange={(e) =>
                      setProfile({ ...profile, experience: e.target.value })
                    }
                  >
                    {experienceLevels.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : (
              <>
                <h2 className="profile-name">{profile.name}</h2>
                <p className="profile-email">{profile.email}</p>
                <div className="profile-details">
                  <span className="detail">
                    <MapPin size={14} />
                    {profile.location}
                  </span>
                  <span className="detail">
                    <Clock size={14} />
                    {profile.experience} experience
                  </span>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Background Section */}
        <section className="background-section">
          <div className="section-header">
            <h2 className="section-title">Your Background</h2>
            <p className="section-description">
              Tell us about your role in property development. This helps Chiefy
              personalize advice for you.
            </p>
          </div>

          <div className="form-group">
            <label htmlFor="profile-background">Role</label>
            <select
              id="profile-background"
              value={background}
              onChange={(e) => setBackground(e.target.value)}
              className="form-select"
            >
              <option value="">Select your role...</option>
              {backgroundOptions.map(bg => (
                <option key={bg} value={bg}>{bg}</option>
              ))}
              {backgroundOptions.length === 0 && (
                <>
                  <option value="Property Developer">Property Developer</option>
                  <option value="Architect">Architect</option>
                  <option value="Project Manager">Project Manager</option>
                  <option value="Student">Student</option>
                  <option value="Other">Other</option>
                </>
              )}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="profile-experience">Experience (optional)</label>
            <select
              id="profile-experience"
              value={profile.focus_areas?.split('|')[0] || ''}
              onChange={(e) => setProfile({ ...profile, focus_areas: e.target.value })}
              className="form-select"
            >
              <option value="">How long have you been working?</option>
              <option value="Less than 1 year">Less than 1 year</option>
              <option value="1-3 years">1-3 years</option>
              <option value="3-5 years">3-5 years</option>
              <option value="5-10 years">5-10 years</option>
              <option value="10-20 years">10-20 years</option>
              <option value="20+ years">20+ years</option>
            </select>
          </div>

          {(profile.focus_areas || profile.desire || profile.anxieties || isEditing) && (
            <div className="background-extended">
              {isEditing ? (
                <>
                  <div className="form-group">
                    <label htmlFor="profile-focus-areas">Focus Areas</label>
                    <input
                      id="profile-focus-areas"
                      type="text"
                      placeholder="e.g. Residential, Commercial, Development"
                      value={profile.focus_areas}
                      onChange={(e) =>
                        setProfile({ ...profile, focus_areas: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="profile-desire">What you&apos;re looking for</label>
                    <textarea
                      id="profile-desire"
                      placeholder="What do you want to achieve?"
                      value={profile.desire}
                      onChange={(e) =>
                        setProfile({ ...profile, desire: e.target.value })
                      }
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="profile-anxieties">Concerns</label>
                    <textarea
                      id="profile-anxieties"
                      placeholder="Any concerns or challenges?"
                      value={profile.anxieties}
                      onChange={(e) =>
                        setProfile({ ...profile, anxieties: e.target.value })
                      }
                      rows={2}
                    />
                  </div>
                </>
              ) : (
                <>
                  {profile.focus_areas && (
                    <p className="background-extended-item">
                      <strong>Focus areas:</strong> {profile.focus_areas}
                    </p>
                  )}
                  {profile.desire && (
                    <p className="background-extended-item">
                      <strong>Looking for:</strong> {profile.desire}
                    </p>
                  )}
                  {profile.anxieties && (
                    <p className="background-extended-item">
                      <strong>Concerns:</strong> {profile.anxieties}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          <div className="background-info-box">
            <div className="info-icon">
              <Lightbulb size={24} />
            </div>
            <div className="info-content">
              <h4>How this helps</h4>
              <p>
                When you search for terminology like "JV", Chiefy will
                prioritize definitions relevant to your background. For example,
                a Property Developer might see urban planning definitions first,
                while a Finance professional might see financial terms.
              </p>
            </div>
          </div>
        </section>

        {/* Subscription Section */}
        <section className="subscription-section">
          <h2 className="section-title">Subscription</h2>

          <button
            type="button"
            className="manage-plan-btn"
            onClick={() => setShowPlanManagement(true)}
          >
            <div className={`manage-plan-icon ${currentPlanDetails.isPremium ? 'premium' : 'basic'}`}>
              {currentPlanDetails.isPremium ? <Zap size={20} /> : <Bot size={20} />}
            </div>
            <div className="manage-plan-info">
              <span className="manage-plan-title">Manage your plan</span>
              <span className="manage-plan-subtitle">
                <span className={`plan-badge-small ${currentPlanDetails.isPremium ? 'premium' : 'basic'}`}>
                  {currentPlanDetails.name}
                </span>
                {currentPlanDetails.price}
              </span>
            </div>
            <ChevronRight size={20} className="manage-plan-arrow" />
          </button>
        </section>

        {/* Preferences */}
        <section className="preferences-section">
          <h2 className="section-title">Preferences</h2>

          <div className="preference-item">
            <div className="preference-info">
              <span className="preference-label">Email Notifications</span>
              <span className="preference-description">
                Receive updates about new courses and networking opportunities
              </span>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                title="Email Notifications"
                checked={preferences.email_notifications}
                onChange={(e) => updatePreference('email_notifications', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="preference-item">
            <div className="preference-info">
              <span className="preference-label">Show Online Status</span>
              <span className="preference-description">
                Let other professionals see when you're online
              </span>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                title="Show Online Status"
                checked={preferences.show_online_status}
                onChange={(e) => updatePreference('show_online_status', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="preference-item">
            <div className="preference-info">
              <span className="preference-label">Dark Mode</span>
              <span className="preference-description">
                Use dark theme throughout the app
              </span>
            </div>
            <label className="toggle">
              <input
                type="checkbox"
                title="Dark Mode"
                checked={preferences.dark_mode}
                onChange={(e) => updatePreference('dark_mode', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </section>
        </>}
      </div>

    </div>
  );
}

export default ProfileView;
